import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import api from "@/lib/api";
import useFormDraft from "@/lib/useFormDraft";
import { useUser } from "@/lib/userContext";

const SKILL = ["Beginner", "Intermediate", "Advanced"];
const COMMIT = ["Part-time evenings", "Weekends", "Full-time"];

const INITIAL_DRAFT = {
    files: [],
    form: {
        full_name: "",
        email: "",
        phone: "",
        location: "",
        education: "",
        portfolio_url: "",
        linkedin: "",
        skill_level: "",
        why_applying: "",
        career_goals: "",
        commitment_level: "",
        file_paths: [],
    },
};

function Field({ label, children, span = "" }) {
    return (
        <div className={`flex flex-col ${span}`}>
            <span className="field-label">{label}</span>
            {children}
        </div>
    );
}

export default function Academy() {
    const { user, signIn } = useUser();
    const [draft, setDraft, clearDraft] = useFormDraft("draft_academy", INITIAL_DRAFT);
    const { form, files } = draft;
    const setForm = (next) => setDraft((d) => ({ ...d, form: typeof next === "function" ? next(d.form) : next }));
    const setFiles = (next) => setDraft((d) => ({ ...d, files: typeof next === "function" ? next(d.files) : next }));

    const [busy, setBusy] = useState(false);
    const [tracking, setTracking] = useState(null);
    const navigate = useNavigate();
    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        const required = ["full_name", "email", "phone", "location", "education", "skill_level", "why_applying", "career_goals", "commitment_level"];
        const missing = required.find((k) => !form[k]?.trim?.());
        if (missing) return toast.error("Please complete all required fields");
        if (!user) {
            toast.message("Sign in to apply", { description: "We'll bring you right back to this form." });
            signIn("/academy");
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form, file_paths: files.map((f) => f.id) };
            const { data } = await api.post("/applications/academy", payload);
            setTracking(data.tracking_id);
            clearDraft();
            toast.success("Application received");
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setBusy(false);
        }
    };

    if (tracking) {
        return (
            <main className="min-h-screen" data-testid="academy-success">
                <Navbar />
                <section className="pt-40 pb-32">
                    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 border is-active mb-8">
                            <Check size={20} />
                        </div>
                        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter mb-6">
                            Application received.
                        </h1>
                        <p className="text-white/65 text-lg mb-4 max-w-xl mx-auto">
                            Pipeline: <span className="text-saffron-brand">Applied → Screening → Test → Interview → Selected</span>
                        </p>
                        <p className="text-white/55 text-sm mb-10 max-w-xl mx-auto">
                            Our team will reach out by email with next steps. Scholarship & fee decisions are made after the interview round and communicated directly to you.
                        </p>
                        <div className="card-surface p-8 inline-block">
                            <div className="eyebrow mb-2">Your Application ID</div>
                            <div className="font-mono text-2xl text-saffron-brand tracking-widest" data-testid="academy-tracking-id">
                                {tracking}
                            </div>
                        </div>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            <button onClick={() => navigate(`/status/${tracking}`)} className="btn-primary" data-testid="aa-track-btn">
                                Track Application <ArrowUpRight size={16} />
                            </button>
                            <button onClick={() => navigate("/my")} className="btn-secondary">
                                Go to My Dashboard
                            </button>
                        </div>
                    </div>
                </section>
                <Footer />
            </main>
        );
    }

    return (
        <main className="min-h-screen" data-testid="academy-page">
            <Navbar />
            <section className="pt-32 pb-16 lg:pt-44 lg:pb-20 border-b border-white/8">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-8">
                        <div className="eyebrow mb-5 text-saffron">Aangan Academy · Selective Program</div>
                        <h1 className="font-display text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
                            Learn. Build. <span className="text-saffron">Lead</span>.
                        </h1>
                        <p className="text-white/65 text-lg max-w-2xl">
                            A selective growth program for future marketers, creators and strategists. Mentor-led.
                            Project-driven. Earned, not paid for.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="card-surface p-6">
                            <div className="eyebrow mb-3">How it works</div>
                            <div className="font-display text-xl text-white mb-3">Apply free.</div>
                            <p className="text-white/55 text-sm leading-relaxed">
                                Application is free. Shortlisted candidates are invited to a test and interview. Fees, scholarships and program details are communicated by email after selection.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <form onSubmit={submit} className="max-w-5xl mx-auto px-6 lg:px-10 space-y-10">
                    <div className="card-surface p-8 lg:p-12">
                        <div className="eyebrow mb-6 text-saffron">01 · Personal</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                            <Field label="Full Name">
                                <input className="field-input" value={form.full_name} onChange={set("full_name")} data-testid="aa-input-full_name" />
                            </Field>
                            <Field label="Email">
                                <input type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="aa-input-email" />
                            </Field>
                            <Field label="Phone">
                                <input className="field-input" value={form.phone} onChange={set("phone")} data-testid="aa-input-phone" />
                            </Field>
                            <Field label="Location">
                                <input className="field-input" value={form.location} onChange={set("location")} data-testid="aa-input-location" />
                            </Field>
                        </div>
                    </div>

                    <div className="card-surface p-8 lg:p-12">
                        <div className="eyebrow mb-6 text-saffron">02 · Background</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                            <Field label="Education">
                                <input className="field-input" value={form.education} onChange={set("education")} placeholder="Most recent degree / institute" data-testid="aa-input-education" />
                            </Field>
                            <Field label="LinkedIn">
                                <input className="field-input" value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/..." data-testid="aa-input-linkedin" />
                            </Field>
                            <Field label="Portfolio URL" span="md:col-span-2">
                                <input className="field-input" value={form.portfolio_url} onChange={set("portfolio_url")} placeholder="Behance, Notion, personal site..." data-testid="aa-input-portfolio_url" />
                            </Field>
                            <Field label="Skill Level" span="md:col-span-2">
                                <div className="flex flex-wrap gap-2">
                                    {SKILL.map((s) => (
                                        <button type="button" key={s} onClick={() => setForm((x) => ({ ...x, skill_level: s }))} className={`tag ${form.skill_level === s ? "is-active" : ""}`} data-testid={`aa-skill-${s}`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Commitment Level" span="md:col-span-2">
                                <div className="flex flex-wrap gap-2">
                                    {COMMIT.map((s) => (
                                        <button type="button" key={s} onClick={() => setForm((x) => ({ ...x, commitment_level: s }))} className={`tag ${form.commitment_level === s ? "is-active" : ""}`} data-testid={`aa-commit-${s}`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                        </div>
                    </div>

                    <div className="card-surface p-8 lg:p-12">
                        <div className="eyebrow mb-6 text-saffron">03 · Why You</div>
                        <div className="space-y-8">
                            <Field label="Why are you applying?">
                                <textarea className="field-input" rows={4} value={form.why_applying} onChange={set("why_applying")} data-testid="aa-input-why_applying" />
                            </Field>
                            <Field label="Career Goals">
                                <textarea className="field-input" rows={4} value={form.career_goals} onChange={set("career_goals")} data-testid="aa-input-career_goals" />
                            </Field>
                            <Field label="Supporting docs (optional)">
                                <FileUpload category="academy" value={files} onChange={setFiles} testid="aa-upload" />
                            </Field>
                        </div>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-6">
                        <div className="text-white/55 text-sm flex items-center gap-2">
                            {!user && <Lock size={12} className="text-saffron-brand" />}
                            Free to apply. Fees and scholarships are decided after the interview round.
                        </div>
                        <button type="submit" disabled={busy} className="btn-primary" data-testid="aa-submit-btn">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
                            {user ? "Submit Application" : "Sign in & Submit"}
                        </button>
                    </div>
                </form>
            </section>
            <Footer />
        </main>
    );
}
